<template>
  <section class="estrut-player">
    <header class="estrut-player__hero">
      <div class="estrut-player__hero-main">
        <div class="estrut-player__meta">
          <span class="estrut-player__meta-icon" aria-hidden="true">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
          <div class="estrut-player__meta-text">
            <h3 class="estrut-player__title">{{ aula.titulo }}</h3>
            <p v-if="aula.descricao" class="estrut-player__desc">{{ aula.descricao }}</p>
          </div>
        </div>

        <div class="estrut-player__action">
          <button
            v-if="!aula.concluida"
            type="button"
            class="estrut-player__complete-btn"
            :disabled="completing"
            @click="markComplete"
          >
            <svg v-if="completing" class="estrut-player__btn-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25" />
              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <svg v-else fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {{ completing ? t('common.loading') : t('estruturacao.lessons.markComplete') }}
          </button>

          <div v-else class="estrut-player__completed-badge">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {{ t('estruturacao.lessons.completed') }}
          </div>
        </div>
      </div>
    </header>

    <div v-if="!hasValidVideo" class="estrut-player__invalid">
      {{ t('estruturacao.lessons.invalidYoutube') }}
    </div>

    <div v-else-if="videoProvider === 'bunny'" class="estrut-player__video-wrap">
      <iframe
        :src="bunnyEmbedUrl"
        class="estrut-player__bunny-iframe"
        loading="lazy"
        allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;fullscreen"
        allowfullscreen
        :title="aula.titulo"
      />
    </div>

    <div v-else ref="videoWrapRef" class="estrut-player__video-wrap">
      <div :id="playerElementId" class="estrut-player__iframe" />
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { extractYoutubeVideoId } from '../../utils/youtubeLink'
import {
  extractBunnyEmbedUrl,
  getLessonVideoProvider
} from '../../utils/lessonVideoLink'
import { loadYoutubeIframeApi } from '../../utils/youtubeIframeApi'
import { saveProgresso } from '../../services/estruturacaoApi'

const props = defineProps({
  aula: { type: Object, required: true }
})

const emit = defineEmits(['progress', 'completed'])

const { t } = useI18n()

const completing = ref(false)
const videoWrapRef = ref(null)
const playerElementId = `estrut-yt-${props.aula.id}`
let player = null
let progressTimer = null
let resizeObserver = null
let resizeDebounce = null
let lastSavedPosition = props.aula.posicaoSegundos || 0
let pendingPosition = lastSavedPosition

const videoProvider = computed(() => getLessonVideoProvider(props.aula.youtubeUrl))
const youtubeVideoId = computed(() => extractYoutubeVideoId(props.aula.youtubeUrl))
const bunnyEmbedUrl = computed(() => extractBunnyEmbedUrl(props.aula.youtubeUrl))
const hasValidVideo = computed(() => videoProvider.value != null)

function getPlayerSize() {
  const wrap = videoWrapRef.value
  const w = wrap?.clientWidth || 640
  const h = Math.round(w * 9 / 16)
  return { w, h }
}

async function initPlayer() {
  if (videoProvider.value !== 'youtube' || !youtubeVideoId.value) return
  await loadYoutubeIframeApi()

  const { w, h } = getPlayerSize()

  player = new window.YT.Player(playerElementId, {
    width: w,
    height: h,
    videoId: youtubeVideoId.value,
    playerVars: {
      enablejsapi: 1,
      rel: 0,
      modestbranding: 1
    },
    events: {
      onReady: onReady,
      onStateChange: onStateChange
    }
  })

  setupResizeObserver()
}

function setupResizeObserver() {
  if (!videoWrapRef.value || typeof ResizeObserver === 'undefined') return
  resizeObserver = new ResizeObserver(() => {
    clearTimeout(resizeDebounce)
    resizeDebounce = setTimeout(resizePlayer, 150)
  })
  resizeObserver.observe(videoWrapRef.value)
}

function resizePlayer() {
  if (!player?.setSize) return
  const { w, h } = getPlayerSize()
  player.setSize(w, h)
}

function onReady() {
  const start = props.aula.posicaoSegundos || 0
  if (start > 0 && player?.seekTo) {
    player.seekTo(start, true)
  }
  resizePlayer()
  startProgressInterval()
}

function onStateChange(event) {
  if (event.data === window.YT.PlayerState.ENDED) {
    handleComplete(true)
  }
  if (event.data === window.YT.PlayerState.PLAYING) {
    startProgressInterval()
  }
}

function startProgressInterval() {
  if (videoProvider.value !== 'youtube') return
  stopProgressInterval()
  progressTimer = setInterval(() => {
    if (!player?.getCurrentTime) return
    const sec = Math.floor(player.getCurrentTime())
    pendingPosition = sec
    emit('progress', { posicaoSegundos: sec })
    if (Math.abs(sec - lastSavedPosition) >= 10) {
      persistProgress(sec, null)
    }
  }, 10000)
}

function stopProgressInterval() {
  if (progressTimer) {
    clearInterval(progressTimer)
    progressTimer = null
  }
}

async function persistProgress(posicaoSegundos, concluida) {
  try {
    await saveProgresso(props.aula.id, {
      posicaoSegundos,
      ...(concluida != null ? { concluida } : {})
    })
    lastSavedPosition = posicaoSegundos
  } catch (e) {
    console.warn('[EstruturacaoPlayer] Erro ao salvar progresso', e)
  }
}

async function markComplete() {
  await handleComplete(false)
}

async function handleComplete(fromVideo) {
  if (completing.value || props.aula.concluida) return
  completing.value = true
  try {
    const sec = player?.getCurrentTime ? Math.floor(player.getCurrentTime()) : pendingPosition
    await saveProgresso(props.aula.id, { posicaoSegundos: sec, concluida: true })
    lastSavedPosition = sec
    emit('completed', { posicaoSegundos: sec, fromVideo })
  } finally {
    completing.value = false
  }
}

function destroyPlayer() {
  stopProgressInterval()
  clearTimeout(resizeDebounce)
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  if (player?.getCurrentTime) {
    const sec = Math.floor(player.getCurrentTime())
    if (Math.abs(sec - lastSavedPosition) >= 1) {
      persistProgress(sec, null)
    }
  }
  if (player?.destroy) {
    player.destroy()
  }
  player = null
}

onMounted(() => {
  initPlayer()
})

onBeforeUnmount(() => {
  destroyPlayer()
})

watch(
  () => props.aula.id,
  () => {
    destroyPlayer()
    initPlayer()
  }
)
</script>

<style scoped>
.estrut-player {
  padding: 0;
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  overflow: hidden;
  box-shadow: 0 4px 24px color-mix(in srgb, #000 8%, transparent);
}

.estrut-player__hero {
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-primary);
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--accent-primary) 8%, var(--bg-primary)),
    var(--bg-primary)
  );
}

.estrut-player__hero-main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.estrut-player__meta {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex: 1;
  min-width: 0;
}

.estrut-player__meta-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 4px;
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--accent-primary) 18%, transparent),
    color-mix(in srgb, var(--accent-primary) 8%, transparent)
  );
  color: var(--accent-primary);
  border: 1px solid color-mix(in srgb, var(--accent-primary) 20%, transparent);
}

.estrut-player__meta-icon svg {
  width: 22px;
  height: 22px;
}

.estrut-player__meta-text {
  flex: 1;
  min-width: 0;
  padding-top: 2px;
}

.estrut-player__title {
  margin: 0;
  font-size: 1.375rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.35;
  color: var(--text-primary);
}

.estrut-player__desc {
  margin: 8px 0 0;
  font-size: 0.875rem;
  line-height: 1.55;
  color: var(--text-secondary);
}

.estrut-player__action {
  flex-shrink: 0;
  padding-top: 4px;
}

.estrut-player__complete-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border: none;
  border-radius: 4px;
  background: linear-gradient(
    135deg,
    var(--accent-primary),
    color-mix(in srgb, var(--accent-primary) 72%, #7c3aed)
  );
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 4px 14px color-mix(in srgb, var(--accent-primary) 32%, transparent);
  transition: transform 0.15s, box-shadow 0.15s, opacity 0.15s;
}

.estrut-player__complete-btn svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.estrut-player__complete-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px color-mix(in srgb, var(--accent-primary) 40%, transparent);
}

.estrut-player__complete-btn:disabled {
  opacity: 0.7;
  cursor: wait;
  transform: none;
}

.estrut-player__btn-spinner {
  animation: estrut-player-spin 0.75s linear infinite;
}

@keyframes estrut-player-spin {
  to { transform: rotate(360deg); }
}

.estrut-player__completed-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 4px;
  background: color-mix(in srgb, #059669 12%, transparent);
  color: #059669;
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid color-mix(in srgb, #059669 28%, transparent);
}

.estrut-player__completed-badge svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

body.tema_escura .estrut-player__completed-badge {
  color: #34d399;
  background: color-mix(in srgb, #34d399 12%, transparent);
  border-color: color-mix(in srgb, #34d399 25%, transparent);
}

.estrut-player__invalid {
  margin: 20px 24px;
  padding: 24px;
  text-align: center;
  color: #dc2626;
  background: color-mix(in srgb, #dc2626 8%, transparent);
  border-radius: 4px;
}

.estrut-player__video-wrap {
  position: relative;
  width: calc(100% - 48px);
  max-width: 960px;
  margin: 20px auto 24px;
  aspect-ratio: 16 / 9;
  max-height: min(56.25vw, 540px);
  background: #000;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 8px 32px color-mix(in srgb, #000 25%, transparent);
}

.estrut-player__iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.estrut-player__iframe :deep(iframe) {
  position: absolute !important;
  inset: 0 !important;
  width: 100% !important;
  height: 100% !important;
}

.estrut-player__bunny-iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

@media (max-width: 768px) {
  .estrut-player__hero-main {
    flex-direction: column;
    align-items: stretch;
  }

  .estrut-player__action {
    padding-top: 0;
  }

  .estrut-player__complete-btn,
  .estrut-player__completed-badge {
    width: 100%;
  }

  .estrut-player__title {
    font-size: 1.125rem;
  }
}
</style>
